import dayjs from "dayjs";
import * as advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat.default);

/**
 * the timezone to use. If not set, the functions will guess it.
 */
let defaultTimezone = undefined;
export const setDefaultTimezone = (timezone) => {
  defaultTimezone = timezone;
};

/**
 * Parses a date to a string format
 * @param date {Date} the date
 * @param format {string} the format
 */
export const getFormattedDate = (date, format, timezone = defaultTimezone) => {
  const tz = timezone || dayjs.tz.guess();
  return dayjs(date).tz(tz).format(format);
};
/**
 * Transform date from timestamp
 * @param timestamp {string} the timestamp
 */
export const getDateFromTimestamp = (timestamp) => {
  return dayjs.unix(+timestamp).toDate();
};

/**
 * Transform date to timestamp
 * @param date {Date} the date
 */
export const getTimestampFromDate = (date) => {
  return dayjs(date).utc().unix() + "";
};

/**
 * Returns the date in the primary timezone, optionally appending
 * the equivalent time in a secondary timezone in brackets with a day offset
 * indicator (e.g. "11:00 (10:00)" or "00:30 (23:30 -1)").
 * @param {Date|string|number} date     the datetime
 * @param {string} primaryTimezone      the main timezone (e.g. user's timezone)
 * @param {string} secondaryTimezone    the secondary timezone (e.g. schedule timezone); pass "local" or falsy to use the browser timezone
 * @param {string} format               the time format string (e.g. TIME_FORMAT)
 * @returns {string}
 */
export const getFormattedDateDoubleTimezone = (
  date,
  format,
  primaryTimezone,
  secondaryTimezone
) => {
  const primaryTimeStr = getFormattedDate(date, format, primaryTimezone);
  if (secondaryTimezone === primaryTimezone) return primaryTimeStr;
  const secondaryTimeStr = getFormattedDate(date, format, secondaryTimezone);

  const dateInSecondaryTz = dayjs(date).tz(secondaryTimezone);
  const dateInPrimaryTz = dayjs(date).tz(primaryTimezone);
  const dayDiff = dayjs(dateInSecondaryTz.format("YYYY-MM-DD")).diff(
    dayjs(dateInPrimaryTz.format("YYYY-MM-DD")),
    "day"
  );
  const dayDiffStr =
    dayDiff !== 0 ? ` (${dayDiff > 0 ? "+" : ""}${dayDiff})` : "";

  return `${primaryTimeStr} (${secondaryTimeStr}${dayDiffStr})`;
};
